import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';
import { resolveIcon } from '../editable/iconRegistry';

const DEFAULT_BENEFITS = [
  {
    id: 'always-full',
    icon: 'Groups',
    title: 'Always-full seats',
    description:
      'Your library appears in search when students look for study spots nearby. New students book and pay upfront.',
    flip: false,
  },
  {
    id: 'digital-payments',
    icon: 'CurrencyRupee',
    title: 'Instant digital payments',
    description:
      'Students pay via UPI, cards, and net banking through Razorpay. Money lands in your wallet. Withdraw anytime.',
    flip: true,
  },
  {
    id: 'qr-checkin',
    icon: 'QrCode2',
    title: 'Tap-to-scan check-in',
    description:
      "Every student gets a personal QR. You scan, they're checked in. No more paper register, no disputes.",
    flip: false,
  },
  {
    id: 'whatsapp-alerts',
    icon: 'WhatsApp',
    title: 'Automated WhatsApp alerts',
    description:
      'Booking confirmations, payment receipts, expiry reminders — sent on WhatsApp automatically.',
    flip: true,
  },
];

/**
 * Alternating icon-left / icon-right benefit rows.
 */
function BenefitsSection({ eyebrow, title, items }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;
  const navy = theme.palette.brand.navy.main;

  const bgAlt = isDark
    ? 'rgba(255,255,255,0.02)'
    : 'rgba(242,101,34,0.025)';

  return (
    <SectionContainer
      eyebrow={eyebrow}
      title={title}
      sx={{
        background: isDark ? theme.palette.background.default : theme.palette.background.default,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 8 } }}>
        {items.map((item, i) => {
          const Icon = resolveIcon(item.icon, 'Groups');
          return (
          <RevealOnScroll key={item.id || item.title} delay={0.05}>
            <Grid
              container
              spacing={{ xs: 4, md: 6 }}
              alignItems="center"
              direction={item.flip ? { xs: 'column', md: 'row-reverse' } : { xs: 'column', md: 'row' }}
            >
              {/* Icon tile */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    width: { xs: 120, md: 160 },
                    height: { xs: 120, md: 160 },
                    borderRadius: '28px',
                    background: `linear-gradient(135deg, ${orange}, ${golden})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: { xs: 'auto', md: item.flip ? 'auto' : 0 },
                    boxShadow: `0 16px 48px rgba(242,101,34,0.25)`,
                  }}
                >
                  <Icon sx={{ color: '#fff', fontSize: { xs: 52, md: 72 } }} />
                </Box>
              </Grid>

              {/* Text */}
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: '20px',
                    background: bgAlt,
                    border: isDark
                      ? '1px solid rgba(255,255,255,0.05)'
                      : '1px solid rgba(27,54,93,0.04)',
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 1.5,
                      fontSize: { xs: '1.35rem', md: '1.65rem' },
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', lineHeight: 1.75 }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </RevealOnScroll>
          );
        })}
      </Box>
    </SectionContainer>
  );
}

BenefitsSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      icon: PropTypes.string,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      flip: PropTypes.bool,
    })
  ),
};

BenefitsSection.defaultProps = {
  eyebrow: 'How we help',
  title: 'Everything you need to grow.',
  items: DEFAULT_BENEFITS,
};

export default BenefitsSection;
